/**
 * QuickLizard Service
 *
 * Self-Registration: Dieser Service registriert sich automatisch beim Import.
 * Consent-Modus: OHNE Usercentrics-Consent (requiresConsent: false)
 *
 * Wird bei page_view und purchase Events getriggert.
 *
 * Um den Service zu entfernen: Einfach den gesamten quick-lizard Ordner löschen
 * und den Import in consent/index.js entfernen.
 */
import { registerEventHandler } from "../../../event-registry";
import serviceRegistry from "../ServiceRegistry";
import quickLizardInteg from "./data-integ";
import quickLizardProd from "./data-prod";

export { initQuickLizard };

function initQuickLizard(dps) {
	// QuickLizard hat keine Init-Logik, reagiert nur auf Events
	// Event-Handler registrieren mit Config-Zugriff
	const { apiUrl } = dps.serviceSettings;
	registerQuickLizardEventHandlers(apiUrl);
}

function registerQuickLizardEventHandlers(apiUrl) {
	// page_view Event für Produktansichten
	// Mandanten-Filter wird über Config (DataProcessingService) geprüft
	// Neue (flache) DataLayer-Struktur: Produktdaten liegen direkt auf dem Model.
	registerEventHandler('page_view', async (model) => {
		// Nur auf Produktseiten ohne Preisbindung.
		// preisbindung kommt als String ("true"/"false") aus dem neuen DataLayer.
		if (model["seitentyp"] !== "produkt" || model["preisbindung"] !== "false") {
			return;
		}
		const ean = model["ean"];
		// Number() parst streng über den ganzen String: "17,39", "-", "k.A."
		// ergeben NaN (im Gegensatz zu parseFloat, das "17,39" → 17 liefern würde).
		// Kein Event verschicken, wenn ean fehlt oder der Preis kein gültiger
		// positiver Betrag ist.
		const price = Number(model["verkaufspreisbrutto"]);
		if (!ean || !(price > 0)) {
			return;
		}
		const payload = trackAds(ean, price);
		await callRestApi(payload, model["stage"], model["base_mandant"], apiUrl);
	}, 'quick-lizard');

	// purchase Event für Conversions
	// Mandanten-Filter wird über Config (DataProcessingService) geprüft
	// Neue (flache) DataLayer-Struktur: Positionen liegen unter ecommerce.items.
	registerEventHandler('purchase', async (model, message) => {
		// Nur auf Dankeseite
		if (model["seitentyp"] !== "dankeseite") {
			return;
		}
		const items = model["ecommerce"]?.["items"];
		if (!Array.isArray(items)) {
			return;
		}
		const payload = trackPurchase(items);
		if (payload.length > 0) {
			await callRestApi(payload, model["stage"], model["base_mandant"], apiUrl);
		}
	}, 'quick-lizard');

}

/*
{
	"client_key": "YOUR_CLIENT_KEY", //your QL client key
	"price": 123.41, //product shelf price as float
	"product_id": "123-AA-X23", //product identifier string
	"event": "view", //event name - "view" represents a product page view
	"permalink": "https://yourstore.com/123-AA-X23/", // product URL on your store
	"vid": "DASDAS-#CCCC-NA", //optional anonymous user identifier
	"tags": ["user:new"], //optional tags for this evening
	"channels": ["online"] //optional pricing channels for this event
}
*/
function trackAds(ean, price) {
	const payload = [
		{
			product_id: ean.toString(),
			price: price,
			event: "view",
			permalink: window.location.href
		}
	];
	return payload;
}

/*
{
  "client_key": "YOUR_CLIENT_KEY", //your QL client key
  "price": 123.41, //product sale price as float
  "product_id": "123-AA-X23", //product identifier string
  "event": "conversion", //event name - "view" represents a product page view
  "vid": "DASDAS-#CCCC-NA", //optional anonymous user identifier
  "tags": ["user:new"], //optional tags for this evening
  "channels": ["online"], //optional pricing channels for this event
  "units": 3 // optional number of sold units
}
*/
function trackPurchase(items) {
	const filteredPayload = items.filter((element) => element.preisbindung !== "true");
	const payload = filteredPayload.map((element) => {
		const units = parseFloat(element.quantity, 10);
		return {
			product_id: element.ean,
			price: parseFloat(element.price, 10) * units,
			units: units,
			event: "conversion",
		}
	});

	return payload;
}

async function callRestApi(payload, stage, baseMandant, apiUrl) {
	if (shouldRequestBeBlocked(navigator.userAgent)) {
		return;
	}
	const basePayload = {
		client_key: "thaliade"
	};
	if (stage === "produktion") {
		switch(baseMandant) {
			case "4":
				basePayload.channels = ["thaliaat"];
				break;
			case "55":
				basePayload.channels = ["bol"];
				break;
			default:
				basePayload.channels = ["default"];
		}
	} else {
		basePayload.channels = ["thaliastaging"];
	}
	const combinedPayload = payload.map((element) => {
		return Object.assign({}, basePayload, element);
	});

	// REST API: https://evt.quicklizard.com/events
	// Wir schicken die Events an die konfigurierte API-URL (typischerweise /proxy/lizard)
	// Im Apache findet sich eine Weiterleitungsregel an https://evt.quicklizard.com/events
	const config = {
		method: 'POST',
		cache: 'no-store',
		body: JSON.stringify(combinedPayload)
	};
	await fetch(apiUrl, config);
}

function shouldRequestBeBlocked(useragent) {
	return /bot|googlebot|crawler|spider|robot|crawling/i.test(useragent)
}

// Self-Registration beim Modulstart
serviceRegistry.registerService({
	name: "QuickLizard",
	initFunction: initQuickLizard,
	requiresConsent: false, // Service lädt ohne Consent
	configs: [quickLizardInteg, quickLizardProd]
});
